import React, { type ChangeEvent, forwardRef, useId, useRef } from "react";
import { BaseInputV2 } from "../BaseInputV2/BaseInputV2.tsx";
import { Skeleton } from "src/components/Skeleton/Skeleton.tsx";
import { InputErrorMessage } from "../InputErrorMessage/InputErrorMessage.tsx";
import * as styles from "./InputV2.styles.ts";
import type { ReactNode } from "react";
import type { BaseInputV2Props } from "../BaseInputV2/BaseInputV2.tsx";
import type { TextTemplatePlaceholder } from "@exonn/js-sdk";
import { Placeholders } from "src/components/InputPlaceholders/InputPlaceholders.tsx";
import { Flex } from "src/components/Flex.tsx";
import { composeRefs } from "@exonn/hooks";
import { useInputInsertion } from "src/components/InputV2/useInputInsertion.ts";
import { InputLabel } from "src/components/InputLabel/InputLabel.tsx";

export type InputV2Props = {
  label?: string;
  error?: string;
  helperText?: string | ReactNode;
  isLoading?: boolean;
  startAdornment?: ReactNode;
  placeholders?: TextTemplatePlaceholder[];
  onSelectPlaceholder?: (placeholder: TextTemplatePlaceholder) => void;
  endAdornment?: ReactNode;
  floatingError?: boolean;
  additionalLabelElems?: ReactNode[];
} & Omit<BaseInputV2Props, "hasError">;

export const InputV2 = forwardRef<HTMLInputElement, InputV2Props>(
  (props, ref) => {
    const {
      label,
      error,
      helperText,
      className,
      autoComplete = "off",
      style,
      additionalLabelElems,
      placeholders,
      value,
      name,
      onChange,
      onSelectPlaceholder,
      floatingError = false,
      isLoading = false,
      required,
      ...baseInputProps
    } = props;

    const {
      id: idProp,
      disabled = false,
      endAdornment,
      startAdornment,
      ...restBaseInputProps
    } = baseInputProps;

    const localId = useId();
    const id = idProp || localId;

    const inputRef = useRef<HTMLInputElement>(null);

    const { saveCursor, restoreCursor, insertText } = useInputInsertion();

    const handleSelectPlaceholder = (placeholder: TextTemplatePlaceholder) => {
      onSelectPlaceholder?.(placeholder);

      if (!inputRef.current) return;

      const code =
        placeholder.type === "conditional"
          ? `{% if ${placeholder.value} %} ... {% endif %}`
          : `{{ ${placeholder.value} }}`;

      const newValue = insertText(inputRef.current, value ?? "", code);

      const syntheticEvent = {
        currentTarget: {
          value: newValue,
          name: name,
        },
        target: {
          value: newValue,
          name: name,
        },
      } as unknown as ChangeEvent<HTMLInputElement>;
      onChange?.(syntheticEvent);

      restoreCursor(inputRef.current);
    };

    return (
      <Skeleton isLoading={isLoading}>
        <div css={styles.containerStyles} style={style} className={className}>
          {label && (
            <InputLabel
              id={id}
              label={label}
              required={required}
              helperText={helperText}
              additionalElems={additionalLabelElems}
            />
          )}
          <Flex flexDirection="column" gap={"15px"}>
            <Placeholders
              onSelectPlaceholder={handleSelectPlaceholder}
              placeholders={placeholders}
            />
            <BaseInputV2
              ref={composeRefs(inputRef, ref)}
              css={styles.inputStyles}
              id={id}
              name={name}
              onSelect={(e) => saveCursor(e.currentTarget)}
              value={value}
              onChange={onChange}
              disabled={disabled}
              hasError={!!error}
              endAdornment={endAdornment}
              startAdornment={startAdornment}
              autoComplete={autoComplete}
              {...restBaseInputProps}
            />
          </Flex>
          {!!error && (
            <InputErrorMessage floating={floatingError} message={error} />
          )}
        </div>
      </Skeleton>
    );
  },
);
